import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { tokenStore, describeApiError } from "../lib/api";
import { Sparkle, ArrowRight, Heart } from "@phosphor-icons/react";

export default function AcceptParentInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadErr, setLoadErr] = useState("");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/public/parent-invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setLoadErr(describeApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (name.trim().length < 2) {
      setError("Please enter your name.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/parent-invite/${token}/accept`, {
        name: name.trim(),
        password,
      });
      if (data.token) tokenStore.set(data.token);
      // Hard reload so the AuthProvider re-runs /auth/me with the new token.
      window.location.href = "/parent";
    } catch (err) {
      setError(describeApiError(err));
      setBusy(false);
    }
  };

  if (loadErr) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF8] px-4">
        <div className="max-w-md text-center" data-testid="parent-invite-error">
          <h1 className="font-outfit text-3xl font-black mb-2">Hmm.</h1>
          <p className="text-slate-600 mb-6">{loadErr}</p>
          <Link to="/login" className="text-yellow-700 underline font-semibold">
            Go to sign-in
          </Link>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="parent-invite-loading">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFFDF8] px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-2xl bg-yellow-400 flex items-center justify-center">
            <Sparkle weight="fill" className="text-yellow-950" size={22} />
          </div>
          <div className="min-w-0">
            <div className="font-outfit font-black text-lg leading-none truncate">
              {invite.class_name || "Alpha AI Discovery Club"}
            </div>
            <div className="text-xs text-slate-500">Parent portal</div>
          </div>
        </div>

        <div className="mb-6" data-testid="parent-invite-hero">
          <div className="inline-flex items-center gap-1 text-xs font-bold tracking-wider uppercase bg-pink-100 text-pink-800 px-2 py-1 rounded-lg mb-3">
            <Heart weight="fill" size={12} /> Parent invite
          </div>
          <h1 className="font-outfit text-4xl font-black tracking-tight">
            Welcome to <span className="text-yellow-700">{invite.student_name}</span>'s parent portal.
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Create a quick sign-in below. You'll see {invite.student_name}'s weekly
            progress, badges, and questions to ask at home.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="parent-invite-form">
          <div>
            <label className="text-sm font-medium block mb-1">Your name</label>
            <input
              type="text"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="parent-invite-name-input"
              autoComplete="name"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
              placeholder="Ama Mensah"
              maxLength={80}
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1">Choose a password</label>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="parent-invite-password-input"
              autoComplete="new-password"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-yellow-400"
              placeholder="At least 6 characters"
            />
            <div className="text-[11px] text-slate-500 mt-1">
              You'll use this to sign in next time.
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2" data-testid="parent-invite-form-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || name.trim().length < 2 || password.length < 6}
            data-testid="parent-invite-submit-btn"
            className="btn-tactile-yellow w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-yellow-400 hover:bg-yellow-500 font-outfit font-bold text-yellow-950 disabled:opacity-60"
          >
            {busy ? "Creating account..." : `Create account & see ${invite.student_name.split(" ")[0]}'s progress`}
            <ArrowRight weight="bold" size={18} />
          </button>
        </form>

        <div className="mt-6 text-xs text-slate-500 text-center">
          Already have a parent account?{" "}
          <Link to="/login" className="font-semibold text-slate-700 underline">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
